import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal, Button, Input, Textarea, Spinner, Tabs } from '../../components/UI';
import { scriptsAPI, productsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { HiOutlineCube } from 'react-icons/hi';

const SECTIONS = [
  { key: 'greeting', label: 'Greeting', placeholder: 'Opening line when the call connects...' },
  { key: 'introduction', label: 'Introduction', placeholder: 'Introduce yourself and the purpose of the call...' },
  { key: 'qualifyingQuestions', label: 'Qualifying Questions', placeholder: 'Questions to qualify the lead (one per line)...' },
  { key: 'objectionHandling', label: 'Objection Handling', placeholder: 'How to handle common objections...' },
  { key: 'closing', label: 'Closing', placeholder: 'Closing line / next steps / call to action...' },
];

const emptyForm = {
  title: '',
  productIds: [],
  content: '',
  greeting: '',
  introduction: '',
  qualifyingQuestions: '',
  objectionHandling: '',
  closing: '',
  isActive: true,
};

const ScriptModal = ({ isOpen, onClose, script, onSuccess }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('full');

  const isEditing = !!script;

  // Fetch active products to populate the multi-select
  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ['products', 'active'],
    queryFn: async () => {
      const response = await productsAPI.getAll({ active: 'true' });
      return response.data?.data || [];
    },
    enabled: isOpen,
  });

  useEffect(() => {
    if (script) {
      setFormData({
        title: script.title || '',
        productIds: (script.productIds || []).map(p => (typeof p === 'object' ? p._id : p)),
        content: script.content || '',
        greeting: script.greeting || '',
        introduction: script.introduction || '',
        qualifyingQuestions: script.qualifyingQuestions || '',
        objectionHandling: script.objectionHandling || '',
        closing: script.closing || '',
        isActive: script.isActive !== false,
      });
    } else {
      setFormData(emptyForm);
    }
    setErrors({});

    // Default to the tab that matches existing data
    if (script) {
      const hasSections = !!(script.greeting || script.introduction ||
        script.qualifyingQuestions || script.objectionHandling || script.closing);
      const hasFull = !!(script.content && script.content.trim());
      setActiveTab(hasFull || !hasSections ? 'full' : 'sections');
    } else {
      setActiveTab('full');
    }
  }, [script, isOpen]);

  const products = productsData || [];

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleProduct = (productId) => {
    setFormData((prev) => {
      const exists = prev.productIds.includes(productId);
      return {
        ...prev,
        productIds: exists
          ? prev.productIds.filter(id => id !== productId)
          : [...prev.productIds, productId],
      };
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title || !formData.title.trim()) {
      newErrors.title = 'Script title is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        title: formData.title.trim(),
        productIds: formData.productIds,
        content: formData.content,
        greeting: formData.greeting,
        introduction: formData.introduction,
        qualifyingQuestions: formData.qualifyingQuestions,
        objectionHandling: formData.objectionHandling,
        closing: formData.closing,
        isActive: formData.isActive,
      };

      if (isEditing) {
        await scriptsAPI.update(script._id, payload);
        toast.success('Script updated successfully');
      } else {
        await scriptsAPI.create(payload);
        toast.success('Script created successfully');
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save script');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Script' : 'Add Script'}
      size="xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Script Title"
          placeholder="e.g. CRM Software - Cold Call Script"
          value={formData.title}
          onChange={handleChange('title')}
          error={errors.title}
          autoFocus
        />

        {/* Product multi-select */}
        <div className="space-y-1.5">
          <label className="block text-sm font-semibold text-secondary-700">
            Products <span className="text-secondary-400 font-normal">(optional — link to one or more)</span>
          </label>
          {productsLoading ? (
            <div className="flex items-center gap-2 text-secondary-500 text-sm py-2">
              <Spinner size="sm" /> Loading products...
            </div>
          ) : products.length === 0 ? (
            <p className="text-sm text-secondary-500 py-2">
              No active products yet. Add products first to link them here.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2 max-h-44 overflow-y-auto p-1">
              {products.map((product) => {
                const selected = formData.productIds.includes(product._id);
                return (
                  <button
                    type="button"
                    key={product._id}
                    onClick={() => toggleProduct(product._id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 text-sm font-medium transition-all ${
                      selected
                        ? 'bg-primary-50 border-primary-400 text-primary-700'
                        : 'bg-white border-secondary-200 text-secondary-600 hover:border-secondary-300'
                    }`}
                  >
                    <HiOutlineCube className="w-4 h-4" />
                    {product.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Script input mode tabs */}
        <div className="pt-2">
          <Tabs
            tabs={[
              { value: 'full', label: 'Full Script' },
              { value: 'sections', label: 'Section-wise Script' },
            ]}
            activeTab={activeTab}
            onChange={setActiveTab}
          />
        </div>

        {activeTab === 'full' ? (
          /* Direct full-text script */
          <div className="space-y-2 pt-3">
            <Textarea
              label="Full Script"
              placeholder="Type the entire call script here in one go..."
              value={formData.content}
              onChange={handleChange('content')}
              rows={10}
            />
            <p className="text-xs text-secondary-400">
              Write the complete script as one block of text.
            </p>
          </div>
        ) : (
          /* Structured sections */
          <div className="space-y-4 pt-3">
            <p className="text-xs text-secondary-400">
              Build the script section by section. Empty sections are skipped.
            </p>
            {SECTIONS.map((section) => (
              <Textarea
                key={section.key}
                label={section.label}
                placeholder={section.placeholder}
                value={formData[section.key]}
                onChange={handleChange(section.key)}
                rows={3}
              />
            ))}
          </div>
        )}

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.isActive}
            onChange={handleChange('isActive')}
            className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm font-semibold text-secondary-700">Active</span>
        </label>

        <div className="flex gap-2 sm:gap-3 justify-end pt-2 border-t border-secondary-100">
          <Button variant="ghost" size="sm" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" type="submit" loading={isSubmitting}>
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ScriptModal;